// Wave 5: Hook-ify the voice + avatar draft state islands previously inlined in
// Composer.tsx. Pure presentation/state hooks — no DB access, no business logic.
import { useCallback, useEffect, useState } from "react";
import type { ContentVoiceProfile } from "@/lib/composerCore";
import {
  parseAuthenticityMarkerRows,
  parseAvoidTermRows,
  parseBuildingBlockProtocolRows,
  parseHighFrequencyTermRows,
  parseMisrepresentationRows,
  parseParagraphFlowRows,
  parsePeakExemplarRows,
  parseRedLineRows,
  parseRhetoricalRows,
  parseSentenceArchitectureRows,
  parseSignaturePhraseRows,
  parseSynthesisRows,
  parseVerbalMarkerRows,
  type VoiceDraftState,
} from "@/components/composer/panels/voiceProfilePanelData";

export type AvatarDraftState = {
  name: string;
  experienceLevel: string;
  summary: string;
  painPoints: string;
  desires: string;
  toneGuidance: string;
  vocabularyAssumptions: string;
  messagingPosture: string;
  voiceModeAWeight: number;
  toneAdjustments: string;
  vocabularyAdjustments: string;
};

const emptyAvatarDraft: AvatarDraftState = {
  name: "",
  experienceLevel: "",
  summary: "",
  painPoints: "",
  desires: "",
  toneGuidance: "",
  vocabularyAssumptions: "",
  messagingPosture: "",
  voiceModeAWeight: 50,
  toneAdjustments: "",
  vocabularyAdjustments: "",
};

const voiceProfileToDraft = (voice: ContentVoiceProfile | null): VoiceDraftState => ({
  title: voice?.title ?? "Master voice profile",
  dual_voice_modes_enabled: voice?.dual_voice_modes_enabled ?? false,
  // Free-form text
  phrase_preferences: voice?.phrase_preferences ?? "",
  avoid_list: parseAvoidTermRows(voice?.avoid_list ?? null),
  // Aethyrius-parity extensions
  core_identity: voice?.core_identity ?? "",
  lexical_compliance: voice?.lexical_compliance ?? "",
  voice_mode_a_label: voice?.voice_mode_a_label ?? "",
  voice_mode_b_label: voice?.voice_mode_b_label ?? "",
  voice_mode_a_notes: voice?.voice_mode_a_notes ?? "",
  voice_mode_b_notes: voice?.voice_mode_b_notes ?? "",
  sentence_architecture: parseSentenceArchitectureRows(voice?.sentence_architecture ?? null),
  paragraph_flow: parseParagraphFlowRows(voice?.paragraph_flow ?? null),
  building_block_protocols: parseBuildingBlockProtocolRows(voice?.building_block_protocols ?? null),
  vocabulary_fingerprint: parseHighFrequencyTermRows(voice?.vocabulary_fingerprint ?? null),
  signature_phrases: parseSignaturePhraseRows(voice?.signature_phrases ?? null),
  verbal_markers: parseVerbalMarkerRows(voice?.verbal_markers ?? null),
  rhetorical_moves: parseRhetoricalRows(voice?.rhetorical_moves ?? null),
  primary_tone: voice?.primary_tone ?? "",
  secondary_tone: voice?.secondary_tone ?? "",
  formality_position: voice?.formality_position ?? "",
  emotional_register: voice?.emotional_register ?? "",
  tradition_synthesis: parseSynthesisRows(voice?.tradition_synthesis ?? null),
  red_lines: parseRedLineRows(voice?.red_lines ?? null),
  common_misrepresentations: parseMisrepresentationRows(voice?.common_misrepresentations ?? null),
  peak_author_examples: parsePeakExemplarRows(voice?.peak_author_examples ?? null),
  authenticity_markers: parseAuthenticityMarkerRows(voice?.authenticity_markers ?? null),
});

/** Mirrors the loaded voice profile into a local editable draft. */
export function useVoiceProfileDraft(voiceProfile: ContentVoiceProfile | null) {
  const [voiceDraft, setVoiceDraft] = useState<VoiceDraftState>(() => voiceProfileToDraft(voiceProfile));

  useEffect(() => {
    setVoiceDraft(voiceProfileToDraft(voiceProfile));
  }, [voiceProfile]);

  return [voiceDraft, setVoiceDraft] as const;
}

/** Local-only "create avatar" form state with a reset helper. */
export function useAvatarDraft() {
  const [avatarDraft, setAvatarDraft] = useState<AvatarDraftState>(emptyAvatarDraft);
  const resetAvatarDraft = useCallback(() => setAvatarDraft(emptyAvatarDraft), []);
  return [avatarDraft, setAvatarDraft, resetAvatarDraft] as const;
}
